import {
  Card,
  CardContent,
  LinearProgress,
  Stack,
  Typography,
  linearProgressClasses,
  styled,
  useTheme,
  type Theme,
} from '@mui/material'
import React from 'react'
import { i18n } from '../../index/index'

// ---------- Types ----------

export type ProgressSection = {
  label?: string
  actual: number
  target: number
  unit?: string
  showValues?: boolean
  showPercentage?: boolean
  showTargetOnly?: boolean
}

export type DashboardCardProps = {
  title: string
  subheader?: React.ReactNode
  sections: ProgressSection[]
  showTotalPercentage?: boolean
  footer?: React.ReactNode
  header?: React.ReactNode
}

// ---------- Constants ----------

const PROGRESS_BAR_HEIGHT = 10
const PROGRESS_BAR_RADIUS = 5
const CARD_MIN_HEIGHT = 220
const THRESHOLD_HIGH = 70
const THRESHOLD_LOW = 40

// ---------- Helpers ----------

const formatNumber = (n: number) => new Intl.NumberFormat('fi-FI', { maximumFractionDigits: 0 }).format(n)

const calcPercentage = (actual: number, target: number) => (target > 0 ? Math.round((actual / target) * 100) : 0)

const getBarColor = (percentage: number, theme: Theme) => {
  if (percentage >= THRESHOLD_HIGH) return theme.palette.progressBar.high
  if (percentage >= THRESHOLD_LOW) return theme.palette.progressBar.mid
  return theme.palette.progressBar.low
}

// ---------- Styled components ----------

const SimpleProgressBar = styled(LinearProgress)(({ theme }) => ({
  height: PROGRESS_BAR_HEIGHT,
  borderRadius: PROGRESS_BAR_RADIUS,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[300],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: PROGRESS_BAR_RADIUS,
  },
}))

const CardShell = styled(Card)(() => ({
  height: '100%',
  minHeight: CARD_MIN_HEIGHT,
  display: 'flex',
  flexDirection: 'column',
}))

const CardBody = styled(CardContent)(() => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
}))

// ---------- Sub-components ----------

type SectionProps = {
  section: ProgressSection
}

const Section: React.FC<SectionProps> = ({ section }) => {
  const { label, actual, target, unit, showValues, showPercentage, showTargetOnly } = section
  const percentage = calcPercentage(actual, target)
  const theme = useTheme()
  const barColor = getBarColor(percentage, theme)

  return (
    <Stack spacing={1}>
      {(label || showPercentage) && (
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          {label && (
            <Typography variant='body2' color='text.secondary'>
              {label}
            </Typography>
          )}
          {showPercentage && (
            <Typography variant='body2' fontWeight={500}>
              {percentage} %
            </Typography>
          )}
        </Stack>
      )}

      <SimpleProgressBar
        variant='determinate'
        value={Math.min(percentage, 100)}
        sx={{ [`& .${linearProgressClasses.bar}`]: { backgroundColor: barColor } }}
      />

      {showValues && (
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Typography variant='body2'>
            {i18n.t('dashboard.widget.actual', { value: formatNumber(actual), unit: unit ?? '' })}
          </Typography>
          <Typography variant='body2'>
            {i18n.t('dashboard.widget.target', { value: formatNumber(target), unit: unit ?? '' })}
          </Typography>
        </Stack>
      )}

      {showTargetOnly && (
        <Stack direction='row' justifyContent='flex-end'>
          <Typography variant='body2'>
            {i18n.t('dashboard.widget.target', { value: formatNumber(target), unit: unit ?? '' })}
          </Typography>
        </Stack>
      )}
    </Stack>
  )
}

// ---------- Main component ----------

const DashboardCard: React.FC<DashboardCardProps> = ({
  title,
  subheader,
  sections,
  showTotalPercentage = false,
  footer,
  header,
}) => {
  const totalActual = sections.reduce((sum, s) => sum + s.actual, 0)
  const totalTarget = sections.reduce((sum, s) => sum + s.target, 0)
  const totalPercentage = calcPercentage(totalActual, totalTarget)

  return (
    <CardShell>
      <CardBody>
        <Stack spacing={1.5} justifyContent='space-between' sx={{ flexGrow: 1 }}>
          <Stack direction='row' alignItems='center' justifyContent='space-between'>
            <Typography variant='h6' fontWeight={500}>
              {title}
            </Typography>
            {showTotalPercentage && (
              <Typography variant='h6' fontWeight={500}>
                {totalPercentage} %
              </Typography>
            )}
            {header}
          </Stack>

          {subheader &&
            (typeof subheader === 'string' ? (
              <Typography variant='body2' color='text.secondary'>
                {subheader}
              </Typography>
            ) : (
              subheader
            ))}

          {sections.map((section, index) => (
            <Section key={section.label ?? index} section={section} />
          ))}

          {footer && (
            <Stack spacing={1} sx={{ mt: 'auto', alignItems: 'flex-end' }}>
              {footer}
            </Stack>
          )}
        </Stack>
      </CardBody>
    </CardShell>
  )
}

export default DashboardCard
